/**
 * 루미나 화면 층 신작 — 블랙 `components/sports/SportsMatchList.jsx` 의 로직 그대로, JSX·CSS 만 교체(시안 스포츠 페이지 리그 카드 .league / 경기 행 .mrow / 배당 .odd / 마켓 펼침 .markets).
 * WHY: SPEC §2 — 그룹화·정렬·점진 로딩·상세 마켓 자동 로드·배당 변동 감지·베팅슬립 담기(addSelection)·마켓 필터 분기는 블랙 그대로 두고,
 *      행의 생김새만 시안대로: [시각 | 홈(팀명·라벨·배당) | 무(기준) | 원정 | +N] 한 줄, 추가 마켓은 행 아래 .markets 패널의 .mkrow 로 펼친다.
 *   · 팀 블록(팀명 클릭 = 그 팀 배당 담기)과 배당 버튼은 블랙처럼 별개 요소(접근성 유지)이되 .odd-grp 하나의 알약으로 묶어 그린다.
 *   · 종목 아이콘은 SportsCategoryList 의 인라인 SVG, 국가는 .cc 국가코드 칩(유저 API league.countryCode 우선) — 이모지 글꼴 없는 환경 대응.
 *   · 라이브 경기(인플레이)는 시각 칸에 LIVE 배지·스코어(REST score / WS score)·진행 시각(REST liveTime / WS matchTime)을 보인다 — 블랙은 실시간 병합만 하고 표시하지 않았다.
 *   · COUNTRY_ISO 표는 SportsCategoryList 로 옮겼다(카테고리 목록도 국가 칩을 그리므로). getCountryFlagUrl 은 홈이 import 하므로 여기 남긴다.
 *
 * 스포츠 경기 목록 - 가로 1행 레이아웃
 * 리그별 그룹화, 가로 배치(시간 | 홈팀 | 배당 | 원정팀 | +N)
 * 리그 헤더: [종목] 국가 / [리그아이콘] 리그명
 */
import React, { useMemo, useState, useCallback, useRef, useEffect } from 'react';
import { useBettingSlip } from '../../contexts/BettingSlipContext';
import sportsService, { MANUAL_ODDS_REFRESH_DELAY_MS } from '../../services/sports';
import { SPORT_ICONS, DefaultSportIcon, COUNTRY_ISO, CountryChip } from './SportsCategoryList';
// 경기 카드를 "마켓별 행"으로 펼쳐 표시하기 위한 순수 함수 유틸.
// marketToRow() 는 market.marketType 에 따라 1행 표시 데이터를 생성하고,
// pickMarketsForRows() 는 펼침 상태와 상세 캐시를 합쳐 표시할 market 목록을 반환한다.
import { marketToRow, pickMarketsForRows } from './MarketRowUtils';

/** 국가명으로 ISO 코드를 찾아 flagcdn.com 국기 URL 반환 (홈 스포츠 티저가 import — 표는 SportsCategoryList.COUNTRY_ISO) */
export const getCountryFlagUrl = (countryName) => {
  if (!countryName) return null;
  const code = COUNTRY_ISO[countryName];
  if (!code) return null;
  return `https://flagcdn.com/w40/${code}.png`;
};

/* ── 인라인 SVG 아이콘 컴포넌트 ── */

/** 스포츠 기본 아이콘 (리그 기본 아이콘 - 트로피 형태) */
const LeagueDefaultIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M7 4h10v3a5 5 0 01-10 0V4z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M17 5h2a2 2 0 012 2v1a3 3 0 01-3 3h-1" stroke="currentColor" strokeWidth="1.5" />
    <path d="M7 5H5a2 2 0 00-2 2v1a3 3 0 003 3h1" stroke="currentColor" strokeWidth="1.5" />
    <line x1="12" y1="13" x2="12" y2="17" stroke="currentColor" strokeWidth="1.5" />
    <rect x="8" y="17" width="8" height="2" rx="1" stroke="currentColor" strokeWidth="1.5" />
  </svg>
);

/** 팀 기본 아이콘 (실드/방패 형태) */
const TeamDefaultIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M12 2L4 6v5c0 5.25 3.4 10.15 8 11.25 4.6-1.1 8-6 8-11.25V6l-8-4z"
      stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" opacity="0.5"
    />
  </svg>
);

/** 배당 등락 삼각형(시안 .tri — ▲ 녹색 / ▼ 빨강). 이모지·특수문자 대신 SVG */
const TriIcon = ({ down = false }) => (
  <svg viewBox="0 0 10 8" aria-hidden="true" className={`tri ${down ? 'down' : 'up'}`}>
    {down ? <path d="M0 0h10L5 8z" /> : <path d="M5 0l5 8H0z" />}
  </svg>
);

/** 스코어 표시 문자열 — REST(parseScoreString: {home, away}) 와 WS(nmsports score 원본: 문자열 또는 객체) 두 형태 모두 수용 */
const formatScore = (score) => {
  if (score == null) return null;
  if (typeof score === 'string') return score.trim() || null;
  if (typeof score === 'object' && score.home != null && score.away != null) return `${score.home} : ${score.away}`;
  return null;
};

/**
 * marketFilter: 'combo' | 'winner' | 'handicap'
 *   - combo    : 기존 동작 — 메인(1X2) 배당 + "+N" 드롭다운
 *   - winner   : 메인(1X2) 배당만, "+N" 드롭다운 숨김
 *   - handicap : 각 경기별 상세 API 로 핸디캡 배당 조회 후 메인 자리에 표시, 드롭다운 숨김
 *   스페셜/라이브 탭일 때는 상위 컴포넌트에서 marketFilter 값과 무관하게 기본 동작.
 */
const SportsMatchList = ({ matches, loading, activeTab, sortMode = 'time', marketFilter = 'combo' }) => {
  const { addSelection, isSelected } = useBettingSlip();
  // 접힌 리그 ID 집합
  const [collapsedLeagues, setCollapsedLeagues] = useState(new Set());
  // 경기별 펼침 상태 — Set<matchId> 로 관리해 여러 경기 동시 펼침 허용.
  // blk-111 참고 사이트와 동일하게, 각 경기를 독립적으로 펼쳐 마켓별 행을 나열한다.
  const [expandedMatches, setExpandedMatches] = useState(() => new Set());
  // 이미지 로드 실패 추적 (key: url)
  const [failedImages, setFailedImages] = useState(new Set());

  // 종목 키(sport_key) → 한글명 매핑 — /sports/types 캐시에서 로드
  // (SportsCategoryList와 sportsService 캐시 공유, 추가 API 호출 없음)
  const [sportTypesMap, setSportTypesMap] = useState({});
  useEffect(() => {
    let cancelled = false;
    const buildMap = (types) => {
      if (!types) return {};
      const map = {};
      types.forEach(t => { map[t.id] = t.name; });
      return map;
    };
    sportsService.fetchSportTypes().then(types => {
      if (cancelled || !types) return;
      setSportTypesMap(buildMap(types));
    });
    const unsubscribe = sportsService.onSportTypesChange(types => {
      if (cancelled || !types) return;
      setSportTypesMap(buildMap(types));
    });
    return () => { cancelled = true; unsubscribe(); };
  }, []);

  // ── 배당 변동 감지 (odds flash animation) ──
  // 이전 배당값 저장 (outcomeId → odds 매핑)
  const prevOddsRef = useRef(new Map());
  // 배당 변동 상태 (outcomeId → 'up' | 'down' | null)
  const [oddsChanges, setOddsChanges] = useState(new Map());

  // [2026-04-21] detailMarketsCache / loadingDetail 선언을 위로 이동.
  //   아래 변동 감지 useEffect 가 detailMarketsCache 를 참조하므로, TDZ 회피를 위해
  //   동일 함수 본문의 선언 순서를 effect 이전으로 정렬한다.
  // 상세 마켓 캐시 — 한 번 로드한 경기의 추가 마켓을 저장
  const [detailMarketsCache, setDetailMarketsCache] = useState({});
  const [loadingDetail, setLoadingDetail] = useState(null);

  // [2026-04-24 SPEC S15c] 스페셜 탭 진입/이탈 시 상세 캐시 무효화.
  //   캐시 키는 matchId 단일이지만 스페셜 탭은 specialOnly=1 로 필터링된 응답을 저장하므로,
  //   탭을 전환하면 이전 모드의 응답이 새 모드로 혼용될 수 있다 (예: 스페셜 탭에서 받은
  //   '스페셜 마켓만' 응답이 승무패 탭에서 재사용되어 배당이 비어 보이는 등).
  //   isSpecialTab 값이 바뀔 때마다 캐시를 비워 두 모드가 분리되게 한다.

  // matches / detailMarketsCache 변경 시 이전 값과 비교하여 변동 감지
  //
  // [2026-04-21] detailMarketsCache 스캔 추가.
  //   언오버·핸디캡 탭은 match.markets(메인 1X2)만으로는 해당 배당을 보여줄 수 없어
  //   경기별 상세 API 결과(detailMarketsCache)를 렌더한다. 기존 구현은 matches 만 스캔해
  //   언오버 탭 outcomeId 의 변동을 prevOddsRef 에 기록하지 못해 ▲/▼ 애니메이션이 절대
  //   트리거되지 않았다. 두 소스의 outcome 을 모두 scanOutcomes 로 훑어 prevOddsRef 를
  //   구성하면 어느 쪽에서 배당이 바뀌어도 동일한 up/down 표시 로직이 동작한다.
  useEffect(() => {
    const newChanges = new Map();
    const newPrevOdds = new Map();

    const scanOutcomes = (markets) => {
      if (!Array.isArray(markets)) return;
      markets.forEach(market => {
        (market?.outcomes || []).forEach(outcome => {
          const key = outcome?.outcomeId;
          if (!key) return;
          const prevOdds = prevOddsRef.current.get(key);
          const currOdds = outcome.odds;

          // 이전 값이 존재하고 현재 값과 다르면 변동 감지
          if (prevOdds !== undefined && prevOdds !== currOdds) {
            newChanges.set(key, currOdds > prevOdds ? 'up' : 'down');
          }
          newPrevOdds.set(key, currOdds);
        });
      });
    };

    (matches || []).forEach(match => scanOutcomes(match.markets));
    Object.values(detailMarketsCache || {}).forEach(markets => scanOutcomes(markets));

    prevOddsRef.current = newPrevOdds;

    if (newChanges.size > 0) {
      // 변동된 배당에 up/down 상태 병합
      setOddsChanges(prev => {
        const merged = new Map(prev);
        newChanges.forEach((v, k) => merged.set(k, v));
        return merged;
      });

      // 1.5초 후 변동 표시 자동 제거
      const keysToRemove = new Map(newChanges);
      setTimeout(() => {
        setOddsChanges(prev => {
          const next = new Map(prev);
          keysToRemove.forEach((_, k) => next.delete(k));
          return next;
        });
      }, 1500);
    }
  }, [matches, detailMarketsCache]);

  /** 리그 접기/펼치기 토글 */
  const toggleLeague = useCallback((leagueId) => {
    setCollapsedLeagues(prev => {
      const next = new Set(prev);
      if (next.has(leagueId)) {
        next.delete(leagueId);
      } else {
        next.add(leagueId);
      }
      return next;
    });
  }, []);

  // [2026-04-21] detailMarketsCache / loadingDetail 선언은 상단 변동 감지 useEffect
  //   이전으로 이동했으므로 여기서는 재선언하지 않는다.
  // ── marketFilter 에 따른 렌더 모드 결정 ──
  // 스페셜/라이브 탭에서는 marketFilter 값을 무시하고 기본(combo) 동작 유지.
  const isLiveTab = activeTab === 'live';
  const isSpecialTab = activeTab === 'prematch_special';
  const isPrematchNormal = !isLiveTab && !isSpecialTab;
  const effectiveMarket = isPrematchNormal ? marketFilter : 'combo';
  const isHandicapMode = effectiveMarket === 'handicap';
  const isOverUnderMode = effectiveMarket === 'overunder';
  const isWinnerMode = effectiveMarket === 'winner';
  // 상세 API 자동 로드가 필요한 모드(핸디캡·언오버·스페셜) — 메인 마켓만으로는 해당 배당을
  // 모두 얻을 수 없어 경기별 상세 API 를 미리 조회해야 한다.
  // [2026-05-06 스페셜 = 언오버 패턴] 스페셜 탭도 상세 자동 로드. 서버 사전 필터 제거에 따라
  //   전체 매치를 받고 클라이언트가 markets 단계에서 feed_type 으로 필터하기 위해 상세 필요.
  const needsDetailAutoload = isHandicapMode || isOverUnderMode || isSpecialTab;
  // combo 모드에서만 "+N" 배지 노출(드롭다운 수동 확장).
  const showMoreBadge = effectiveMarket === 'combo';

  // [2026-04-24 SPEC S15c] isSpecialTab 변경 시 상세 캐시 무효화 (상단 주석 참조)
  useEffect(() => {
    setDetailMarketsCache({});
  }, [isSpecialTab]);

  // [2026-10-18 W93] 손등록 경기 배당·마켓 변경 알림(sportsManualOdds) → 받아 둔(펼친·자동 로드한) 그 경기 마켓을 다시 받는다.
  //   WHY: 펼친 마켓은 처음 받은 배당을 계속 보여 줘, 운영자가 배당을 고쳐도 회원은 예전 배당을 봤다.
  //   받아 둔 적 없는 경기는 펼칠 때 받는다. 실패하면 지금 캐시를 그대로 둔다. 캐시·스페셜 탭 여부는 최신 값을 참조로 읽는다.
  const detailCacheRef = useRef(detailMarketsCache);
  detailCacheRef.current = detailMarketsCache;
  const specialTabRef = useRef(isSpecialTab);
  specialTabRef.current = isSpecialTab;
  useEffect(() => {
    const timers = new Map();
    const handler = (event) => {
      const fid = event.detail?.data?.fixtureId;
      if (fid == null) return;
      const matchId = `fixture-manual-${fid}`;
      if (timers.has(matchId)) clearTimeout(timers.get(matchId));
      timers.set(matchId, setTimeout(async () => {
        timers.delete(matchId);
        if (!detailCacheRef.current[matchId]) return;
        const d = await sportsService.getMatchDetail(matchId, { specialOnly: specialTabRef.current });
        if (d && Array.isArray(d.markets)) {
          setDetailMarketsCache(prev => (prev[matchId] ? { ...prev, [matchId]: d.markets } : prev));
        }
      }, MANUAL_ODDS_REFRESH_DELAY_MS));
    };
    window.addEventListener('sportsManualOdds', handler);
    return () => { timers.forEach(t => clearTimeout(t)); window.removeEventListener('sportsManualOdds', handler); };
  }, []);

  // ── 상세 마켓 자동 로드 (동시성 제한) ──
  // 핸디캡·언오버 탭 활성 + matches 변경 시, 캐시 없는 경기의 상세 마켓을 조회.
  // 메인 마켓(승무패 1X2) 만으로는 핸디캡·오버언더 배당이 부족하므로 상세 API 선조회 필요.
  // 경기 수가 많을 때 모든 요청을 한꺼번에 보내면 브라우저 동시 연결 한도(도메인당 6~8)를
  // 초과하여 ERR_INSUFFICIENT_RESOURCES 가 발생한다. 배치 크기 6 으로 순차 처리하고
  // 배치 완료마다 캐시를 점진 업데이트해 먼저 로드된 경기부터 UI 에 반영되게 한다.
  useEffect(() => {
    if (!needsDetailAutoload || !Array.isArray(matches) || matches.length === 0) return;
    const toLoad = matches.filter(m => m?.matchId && !detailMarketsCache[m.matchId]);
    if (toLoad.length === 0) return;

    let cancelled = false;
    const BATCH_SIZE = 6;

    const loadInBatches = async () => {
      for (let i = 0; i < toLoad.length; i += BATCH_SIZE) {
        if (cancelled) return;
        const batch = toLoad.slice(i, i + BATCH_SIZE);
        const batchResults = await Promise.all(
          batch.map(m =>
            // [2026-04-24 SPEC S15c] 스페셜 탭이면 specialOnly 전달 → 서버가 A/B/C 규칙으로 마켓 필터.
            sportsService.getMatchDetail(m.matchId, { specialOnly: isSpecialTab })
              .then(d => ({ id: m.matchId, markets: Array.isArray(d?.markets) ? d.markets : [] }))
              .catch(() => ({ id: m.matchId, markets: [] }))
          )
        );
        if (cancelled) return;
        // 배치 결과를 즉시 캐시에 반영 — 먼저 로드된 경기부터 UI 에 배당 표시
        setDetailMarketsCache(prev => {
          const next = { ...prev };
          batchResults.forEach(r => { next[r.id] = r.markets; });
          return next;
        });
      }
    };
    loadInBatches();

    return () => { cancelled = true; };
  }, [needsDetailAutoload, matches, detailMarketsCache]);

  /**
   * 마켓명이 핸디캡 류인지 판별 (한글 '핸디캡' 또는 영문 'handicap' 포함).
   * 백엔드 MARKET_NAME_MAP 에서 '핸디캡' / '3-Way 핸디캡' / '전반 핸디캡' 등으로 반환됨.
   */
  const isHandicapMarket = (market) => {
    if (!market || !market.marketName) return false;
    const name = String(market.marketName);
    return name.includes('핸디캡') || name.toLowerCase().includes('handicap');
  };

  /**
   * 경기 펼침 토글 + 상세 마켓 로드.
   * 여러 경기를 동시에 펼칠 수 있도록 Set 으로 관리.
   * 이미 펼쳐진 경기를 다시 클릭하면 접기 (캐시는 유지해 재조회 비용 절감).
   */
  const toggleExpandedMatch = useCallback(async (matchId, e) => {
    e.stopPropagation();

    // 이미 펼쳐진 경기는 접기
    if (expandedMatches.has(matchId)) {
      setExpandedMatches(prev => {
        const next = new Set(prev);
        next.delete(matchId);
        return next;
      });
      return;
    }

    // 펼침 상태 갱신
    setExpandedMatches(prev => {
      const next = new Set(prev);
      next.add(matchId);
      return next;
    });

    // 캐시에 이미 있으면 재로드 불필요
    if (detailMarketsCache[matchId]) return;

    // 상세 마켓 로드 (/sports/matches/:matchId → /fixtures/:id/odds)
    setLoadingDetail(matchId);
    try {
      // [2026-04-24 SPEC S15c] 스페셜 탭일 때 specialOnly=1 전송
      const detail = await sportsService.getMatchDetail(matchId, { specialOnly: isSpecialTab });
      if (detail && detail.markets) {
        setDetailMarketsCache(prev => ({ ...prev, [matchId]: detail.markets }));
      }
    } catch (err) {
      console.warn('[SportsMatchList] 상세 마켓 로드 실패:', matchId, err.message);
    } finally {
      setLoadingDetail(null);
    }
  }, [expandedMatches, detailMarketsCache, isSpecialTab]);

  // [2026-05-06 스페셜 카운트 정합] isSpecialTab 일 때 hasSpecialMarket=true 매치만
  //   그룹화·노출 대상으로 사용. SportsCategoryList 의 카운트 계산도 동일 기준이라 정합.
  //   admin-api /fixtures/table 후처리 + user-api adaptFixtureTable 이 hasSpecialMarket
  //   boolean 을 부착. admin-api 응답에 누락 시 false 폴백 → 기존 동작 유지.
  const baseMatches = useMemo(() => {
    if (!isSpecialTab) return matches;
    if (!Array.isArray(matches)) return matches;
    return matches.filter(m => m && m.hasSpecialMarket === true);
  }, [matches, isSpecialTab]);

  // 리그별 경기 그룹화 (sortMode === 'league' 일 때 사용)
  // WHY: 일반 객체로 group 하면 leagueId 가 정수 형태 문자열(예: "1001") 일 때
  //   ECMAScript [[OwnPropertyKeys]] 명세상 Object.values 가 정수 키를 숫자 ASC
  //   순서로 강제 반환한다. 그 결과 백엔드 시간 ASC 응답이 league_gs_id 숫자 ASC
  //   순서로 뒤집혀 노출되는 버그가 있었다 (예: LNB(1001) 첫 경기 05-01 03:00
  //   이 KHL(1002) 첫 경기 04-30 01:30 보다 위에 표시). Map 은 키 타입 무관
  //   insertion order 를 보장하므로, 시간 ASC 응답이 그대로 유지된다.
  const groupedMatches = useMemo(() => {
    const groups = new Map();
    (baseMatches || []).forEach(match => {
      const key = match.league.leagueId;
      if (!groups.has(key)) {
        groups.set(key, { league: match.league, matches: [], sportType: match.sportType });
      }
      groups.get(key).matches.push(match);
    });
    return [...groups.values()];
  }, [baseMatches]);

  // 시간순 정렬 경기 목록 (sortMode === 'time' 일 때 사용)
  // [tie-break 2026-04-18] 동일 시각 경기는 matchId('fixture-{id}') 의 숫자 오름차순.
  //   관리자 경기관리와 동일 규칙을 사용해 같은 경기가 양쪽에서 같은 순서로 보이도록 한다.
  const timeSortedMatches = useMemo(() => {
    if (sortMode !== 'time') return [];
    return [...(baseMatches || [])].sort((a, b) => {
      const d = new Date(a.startTime) - new Date(b.startTime);
      if (d !== 0) return d;
      // [2026-04-24 SPEC S9] 자동/수동 matchId 양 포맷 지원 (fixture-X / fixture-manual-X)
      const ai = Number(String(a.matchId || '').replace(/^fixture-(?:manual-)?/, '')) || 0;
      const bi = Number(String(b.matchId || '').replace(/^fixture-(?:manual-)?/, '')) || 0;
      return ai - bi;
    });
  }, [baseMatches, sortMode]);

  // 국가별 그룹화 (sortMode === 'country' 일 때 사용)
  // WHY: 현재 키는 한글 국가명(예: '일본', '러시아')이라 정수 키 ASC 강제 정렬
  //   영향이 없지만, groupedMatches 와 동일하게 Map 사용으로 통일한다. 향후 키가
  //   ISO 코드/숫자 ID 등으로 바뀌어도 insertion order 가 보장되도록 안전성 확보.
  const countryGroupedMatches = useMemo(() => {
    if (sortMode !== 'country') return [];
    const groups = new Map();
    (baseMatches || []).forEach(match => {
      const country = match.league?.country || '기타';
      if (!groups.has(country)) {
        groups.set(country, { country, matches: [], sportType: match.sportType });
      }
      groups.get(country).matches.push(match);
    });
    // 각 국가 그룹 내에서 시간순 정렬
    return [...groups.values()].map(group => ({
      ...group,
      matches: group.matches.sort(
        (a, b) => new Date(a.startTime) - new Date(b.startTime)
      ),
    }));
  }, [matches, sortMode]);

  // ── 점진 로딩 (Progressive Loading) ──
  // 초기에 INITIAL_VISIBLE 개만 DOM에 렌더하고, 스크롤 시 LOAD_MORE 씩 추가.
  // 프리매치 ~560개 카드를 한번에 그리면 17k+ DOM 노드로 초기 렌더 1.7초 longtask 발생.
  // IntersectionObserver로 센티널이 뷰포트에 진입하면 자동 확장.
  const INITIAL_VISIBLE = 30;
  const LOAD_MORE = 30;
  const [maxVisible, setMaxVisible] = useState(INITIAL_VISIBLE);
  const sentinelRef = useRef(null);
  const totalMatches = matches?.length || 0;
  const hasMore = maxVisible < totalMatches;

  // 데이터 구조 변경(필터/종목 전환) 시 리셋, 배당 실시간 업데이트(count 동일) 시 유지
  useEffect(() => {
    setMaxVisible(INITIAL_VISIBLE);
  }, [totalMatches, sortMode]);

  // IntersectionObserver — 스크롤 하단 300px 전에 다음 배치 로드
  // root=null(viewport) — 실제 스크롤은 window/body 레벨에서 발생.
  // hasMore 의존: 초기 로드 시 matches=[]이면 센티널이 없으므로,
  // 데이터 도착 후 hasMore=true가 되면 옵저버를 재설정한다.
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setMaxVisible(prev => prev + LOAD_MORE);
      }
    }, { rootMargin: '300px' });
    obs.observe(el);
    return () => obs.disconnect();
  }, [hasMore]);

  // 리그별 모드: 그룹 순서 유지하되 maxVisible까지만 카드 렌더
  const limitedGrouped = useMemo(() => {
    let remaining = maxVisible;
    const result = [];
    for (const group of groupedMatches) {
      if (remaining <= 0) break;
      const count = Math.min(group.matches.length, remaining);
      result.push({ ...group, _visibleMatches: group.matches.slice(0, count) });
      remaining -= count;
    }
    return result;
  }, [groupedMatches, maxVisible]);

  // 국가별 모드: 동일 패턴
  const limitedCountry = useMemo(() => {
    let remaining = maxVisible;
    const result = [];
    for (const group of countryGroupedMatches) {
      if (remaining <= 0) break;
      const count = Math.min(group.matches.length, remaining);
      result.push({ ...group, _visibleMatches: group.matches.slice(0, count) });
      remaining -= count;
    }
    return result;
  }, [countryGroupedMatches, maxVisible]);

  /** 배당 클릭 핸들러 */
  /**
   * [2026-10-22 W93 후속 5차] 잠긴 마켓인가 — 경기관리 '잠금'(경기 단위)이면 회원 API 가 그 경기의 마켓 상태를 'suspended' 로 보낸다
   *   (수동 routes/sports.js adaptManualFixture · 자동 같은 파일 자동 경기 변환 — 자동·수동 공통). 배당 서버가 정지한 마켓은 회원 API 가
   *   목록에서 빼므로 여기까지 오지 않는다.
   * WHY: 종전에는 잠긴 마켓도 배당 단추·팀 블록이 평소처럼 눌려 슬립에 담기고, '베팅하기'에서야 서버가 "잠금 상태입니다"로 거절했다.
   *   잠긴 마켓은 배당을 흐리게 보이고 누를 수 없게 한다. 접수 거절은 서버가 그대로 한다 — 이 판정은 서버 상태를 화면에 비추는 안내일 뿐
   *   (서버가 받는 베팅을 화면에서 막는 것이 아니다).
   */
  const isMarketLocked = (market) => !!market && String(market.status || '').toLowerCase() === 'suspended';

  const handleOddsClick = (match, market, outcome) => {
    if (isMarketLocked(market)) return; // [W93 후속 5차] 잠긴 마켓은 담지 않는다(위 isMarketLocked)
    // 해당 마켓의 배당 전체 추출 — 이름 기반 매칭 (인덱스 매칭은 nmsports 순서 보장 안 돼서 위험)
    // outcome.name은 백엔드 getOutcomeName으로 한글화된 값('승'/'무'/'패' 또는 '오버'/'언더')
    const outcomes = market.outcomes || [];
    const findByNames = (names) => outcomes.find(o => names.includes(o.name));
    const homeOutcome = findByNames(['승', 'Home', '1']);
    const drawOutcome = findByNames(['무', 'Draw', 'X']);
    const awayOutcome = findByNames(['패', 'Away', '2']);

    // 마켓이 어떤 selection 인지 식별 (선언적으로 — outcomeId.split 의존 제거)
    let selectionKey = null;
    if (outcome === homeOutcome) selectionKey = 'home';
    else if (outcome === drawOutcome) selectionKey = 'draw';
    else if (outcome === awayOutcome) selectionKey = 'away';
    // 위 매칭이 안 되면 outcome.name 자체 사용 (오버/언더, 핸디캡, 스코어 등)

    addSelection({
      matchId: match.matchId,
      goalserveFixtureId: match.goalserveFixtureId || null,
      marketId: market.marketId,
      // [2026-05-04] marketDbId — 동일경기 조합 차단 가드용 sports_markets.id 정수.
      //   user-api 응답에서 받은 신뢰값을 그대로 보존해 클라/서버 가드가 정합 비교.
      marketDbId: market.marketDbId ?? null,
      outcomeId: outcome.outcomeId,
      matchInfo: `${match.homeTeam.name} vs ${match.awayTeam.name}`,
      marketName: market.marketName,
      outcomeName: outcome.name,
      // 베팅 정산에 사용할 명시적 selection 키 (백엔드가 outcomeId 파싱에 의존하지 않도록)
      selectionKey,
      // WHY: nmsports-odds 원본 market.line 값을 베팅 INSERT 까지 보존.
      //   핸디캡/언오버의 기준치(예: -1.5, 5.5)를 sports_bet_details.bet_line
      //   컬럼에 저장해 셀별 베팅금 분리·자동 정산을 가능케 한다.
      //   라인 없는 마켓(승무패 등)은 user-api 가 null 로 내려주므로 그대로 전달.
      line: market.line ?? null,
      odds: outcome.odds,
      // 마켓 배당 전체 — 이름 기반으로 정확한 매핑 (인덱스 의존 제거)
      oddsHome: homeOutcome?.odds ?? null,
      oddsDraw: drawOutcome?.odds ?? null,
      oddsAway: awayOutcome?.odds ?? null,
      // 백엔드 베팅 처리에 필요한 경기 메타데이터
      startTime: match.startTime,
      sportType: match.sportType,
      leagueId: match.league?.leagueId || null,
      betType: match.betType,
      // 베팅내역 표시에 필요한 경기 정보
      countryName: match.league?.country || null,
      leagueName: match.league?.name || null,
      matchStatus: match.matchStatus || null,
      // 로고 표시에 필요한 GoalServe ID
      leagueGsId: match.league?.leagueGsId || null,
      homeTeamId: match.homeTeam?.id || null,
      awayTeamId: match.awayTeam?.id || null,
    });
  };

  /** 시간 포맷 (모바일용 분리 표시) */
  const formatTime = (iso) => {
    const d = new Date(iso);
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    const ss = String(d.getSeconds()).padStart(2, '0');
    return `${hh}:${mm}:${ss}`;
  };

  const formatDate = (iso) => {
    const d = new Date(iso);
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${mm}/${dd}`;
  };

  /**
   * "MM-DD HH:MM" 형식 — 경기 행 시간 표시용.
   * blk-111 참고 사이트와 동일한 표기.
   */
  const formatMatchDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    const MM = String(d.getMonth() + 1).padStart(2, '0');
    const DD = String(d.getDate()).padStart(2, '0');
    const HH = String(d.getHours()).padStart(2, '0');
    const mi = String(d.getMinutes()).padStart(2, '0');
    return `${MM}-${DD} ${HH}:${mi}`;
  };


  /**
   * 메인 마켓 찾기 (방어 로직)
   *
   * 백엔드에서 수백 개 마켓을 그룹핑 없이 보내는 경우 대비:
   * 1순위: marketType이 'match_result'인 마켓
   * 2순위: marketName이 승무패/승패/Fulltime Result/Match Winner 인 마켓
   * 3순위: outcomes가 2~3개인 첫 번째 마켓 (일반적인 메인 마켓 형태)
   * 4순위: markets[0] (fallback)
   */
  const findMainMarket = useCallback((markets) => {
    if (!markets || markets.length === 0) return null;

    // 1순위: marketType === 'match_result'
    const byType = markets.find(m => m.marketType === 'match_result');
    if (byType) return byType;

    // 2순위: marketName 기반 (한국어/영어)
    const mainMarketNames = ['승무패', '승패', 'fulltime result', 'match winner', '1x2', 'moneyline'];
    const byName = markets.find(m =>
      mainMarketNames.some(name => m.marketName?.toLowerCase().includes(name))
    );
    if (byName) return byName;

    // 3순위: outcomes 2~3개인 첫 번째 마켓 (메인 마켓의 일반적 형태)
    const byOutcomeCount = markets.find(m =>
      m.outcomes && m.outcomes.length >= 2 && m.outcomes.length <= 3
    );
    if (byOutcomeCount) return byOutcomeCount;

    // 4순위: fallback - markets[0]
    return markets[0];
  }, []);

  /** 이미지 로드 실패 핸들러 */
  const handleImageError = useCallback((url) => {
    setFailedImages(prev => {
      const next = new Set(prev);
      next.add(url);
      return next;
    });
  }, []);

  /** 이미지 로드 성공 핸들러 — 1x1 투명 PNG(fallback)이면 실패로 처리 */
  const handleImageLoad = useCallback((e, url) => {
    if (e.target.naturalWidth <= 1) handleImageError(url);
  }, [handleImageError]);

  /**
   * 팀 로고 렌더링
   * - team.id가 유효하면(0이 아닌 양수) 프록시 이미지 시도
   * - 이미지 실패 또는 id 없으면 기본 SVG 아이콘
   */
  const renderTeamLogo = useCallback((team, sportType) => {
    const teamId = team?.id;
    // team_id가 유효한 경우 (0이 아닌 양수)
    if (teamId && teamId !== 0 && teamId !== '0') {
      const imgUrl = `/api/sports/logos/team/${sportType || 'soccer'}/${teamId}`;
      if (!failedImages.has(imgUrl)) {
        return (
          <img
            src={imgUrl}
            alt=""
            className="odd-logo"
            onError={() => handleImageError(imgUrl)}
            onLoad={(e) => handleImageLoad(e, imgUrl)}
            loading="lazy"
          />
        );
      }
    }
    // 기본 아이콘 (ID 없거나 이미지 실패 시)
    return (
      <span className="odd-logo odd-logo-default">
        <TeamDefaultIcon size={16} />
      </span>
    );
  }, [failedImages, handleImageError, handleImageLoad]);

  /**
   * 리그 아이콘 렌더링
   * - league_id가 유효하면(0이 아닌 값) 프록시 이미지 시도
   * - 실패 시 또는 ID 없으면 기본 트로피 아이콘
   */
  const renderLeagueIcon = useCallback((league, sportType) => {
    const leagueId = league?.leagueId;
    // 숫자 형태 ID이고 0이 아닌 경우만 이미지 시도
    const numericId = Number(leagueId);
    if (!isNaN(numericId) && numericId > 0) {
      const imgUrl = `/api/sports/logos/league/${sportType || 'soccer'}/${leagueId}`;
      if (!failedImages.has(imgUrl)) {
        return (
          <img
            src={imgUrl}
            alt=""
            className="league-icon-img"
            onError={() => handleImageError(imgUrl)}
            onLoad={(e) => handleImageLoad(e, imgUrl)}
            loading="lazy"
          />
        );
      }
    }
    // 기본 리그 아이콘
    return <LeagueDefaultIcon size={15} />;
  }, [failedImages, handleImageError, handleImageLoad]);

  /**
   * 종목 뱃지 렌더링 — 아이콘 + 한글명
   * SPORT_ICONS는 SportsCategoryList에서 import (sport_key 키 기반 SVG 세트)
   * 매칭 없는 종목은 DefaultSportIcon으로 폴백, 한글명 없으면 키 자체 표시
   */
  const renderSportBadge = useCallback((sportType) => {
    if (!sportType) return null;
    return (
      <span className="sp-badge">
        <span className="flag">
          {SPORT_ICONS[sportType] || <DefaultSportIcon />}
        </span>
        <span className="sp-badge-name">
          {sportTypesMap[sportType] || sportType}
        </span>
      </span>
    );
  }, [sportTypesMap]);

  // 로딩 상태
  if (loading) {
    return (
      <div className="sp-loading" role="status">
        <span className="sp-spin" aria-hidden="true" />
        <p>경기 정보를 불러오는 중...</p>
      </div>
    );
  }

  // 빈 상태
  if (!matches || matches.length === 0) {
    return (
      <div className="sp-empty sp-empty-list">
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <circle cx="12" cy="12" r="9" /><path d="M8 12.5l2.5 2.5L16 9.5" />
        </svg>
        <p className="sp-empty-title">
          {activeTab === 'live' ? '진행 중인 라이브 경기가 없습니다' : '등록된 경기가 없습니다'}
        </p>
        <p className="sp-empty-desc">배당 API 연동 후 실시간 경기 정보가 표시됩니다</p>
      </div>
    );
  }

  /**
   * 팀블록(홈/원정) 클릭 가능 여부 판정 — outcome 이 있고 odds 가 유효해야 함.
   * WHY: 사용자 요구(이미지 #55) — 배당 숫자뿐 아니라 팀 이름 영역을 찍어도
   *      해당 팀 배당이 베팅슬립에 담겨야 한다. 클릭 가능 시에만 pointer 커서와
   *      키보드 접근성(role="button", tabIndex)을 부여해 접근성 혼선 방지.
   */
  const isTeamBlockClickable = (outcome) =>
    !!(outcome && outcome.odds != null);

  /**
   * 팀블록 onClick/onKeyDown 공통 바인딩 생성기. 비활성 시 undefined 반환하여
   * Enter/Space 키 의도치 않은 트리거를 막는다.
   */
  const makeTeamBlockHandlers = (match, market, outcome) => {
    if (!isTeamBlockClickable(outcome) || isMarketLocked(market)) return null; // [W93 후속 5차] 잠긴 마켓은 팀 블록도 누를 수 없다
    const handler = () => handleOddsClick(match, market, outcome);
    return {
      onClick: handler,
      onKeyDown: (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handler();
        }
      },
      role: 'button',
      tabIndex: 0,
    };
  };

  /**
   * 단일 배당 버튼 렌더러 — 행 각 칸의 배당 숫자에서 공통 사용.
   * 기존 oddsChanges(실시간 깜빡임) / isSelected(베팅슬립 선택 하이라이트) /
   * handleOddsClick(addSelection 연동) 로직을 그대로 재사용해
   * 기존 기능(실시간 배당 WS 반영, 베팅슬립 싱글/멀티 카트) 을 깨지 않는다.
   * outcome 이 없거나 odds 가 없으면 '-' 비활성 칸을 보여 레이아웃 흔들림 방지.
   */
  const renderOddsButton = (match, market, outcome, badge) => {
    if (!outcome || outcome.odds == null) {
      return <span className="odd-val disabled">-</span>;
    }
    // [W93 후속 5차] 잠긴 마켓 — 배당은 흐리게 보이고 누를 수 없다(위 isMarketLocked)
    if (isMarketLocked(market)) {
      return (
        <span className="odd-val disabled" title="잠금 — 지금은 베팅할 수 없습니다" aria-disabled="true">
          <b>{Number(outcome.odds).toFixed(2)}</b>
        </span>
      );
    }
    const oc = outcome.outcomeId;
    const selected = isSelected(oc);
    const change = oddsChanges.get(oc);
    const cls = [
      'odd-val',
      selected ? 'on' : '',
      change === 'up' ? 'up' : '',
      change === 'down' ? 'down' : '',
    ].filter(Boolean).join(' ');
    // [2026-04-21] OVER/UNDER 는 MarketRowUtils 가 'OVER_ARROW'/'UNDER_ARROW' 마커를 넘긴다.
    //   이 경우 오버(▲)·언더(▼) 방향 아이콘을 배당 앞에 그린다. 기타 텍스트 배지(HA/AH/X)는
    //   시안 규칙대로 그리지 않는다 — 같은 정보(마켓 종류)는 팀명 옆 .lbl 마켓 라벨이 전달한다.
    let badgeEl = null;
    if (badge === 'OVER_ARROW') {
      badgeEl = <span className="odd-dir odd-dir-over" aria-label="오버"><TriIcon /></span>;
    } else if (badge === 'UNDER_ARROW') {
      badgeEl = <span className="odd-dir odd-dir-under" aria-label="언더"><TriIcon down /></span>;
    }

    return (
      <button
        type="button"
        className={cls}
        onClick={() => handleOddsClick(match, market, outcome)}
      >
        {badgeEl}
        <b>{Number(outcome.odds).toFixed(2)}</b>
        {/* 실시간 변동 표시(1.5초): ▲ 상승 / ▼ 하락 — 시안 .tri */}
        {change === 'up' && <TriIcon />}
        {change === 'down' && <TriIcon down />}
      </button>
    );
  };

  /**
   * 홈/원정 한 쪽 = 알약 하나(.odd-grp): [로고·팀명·마켓 라벨(팀 블록, 클릭 = 배당 담기)] + [배당 버튼].
   * 팀 블록 선택 상태(isSelected)를 알약 전체 색(.on)으로 보여 홈/원정/무 세 칸의 피드백을 통일한다.
   */
  const renderSideGroup = (match, market, outcome, badge, side, team, label) => {
    const handlers = makeTeamBlockHandlers(match, market, outcome);
    const selected = outcome ? isSelected(outcome.outcomeId) : false;
    const grpCls = [
      'odd-grp',
      side === 'home' ? 'odd-home' : 'odd-away',
      handlers ? 'clickable' : '',
      selected ? 'on' : '',
    ].filter(Boolean).join(' ');
    return (
      <div className={grpCls}>
        <div className="odd-team" {...(handlers || {})}>
          {renderTeamLogo(team, match.sportType)}
          <span className="tm">{team?.name}</span>
          {label && <span className="lbl">{label}</span>}
        </div>
        {renderOddsButton(match, market, outcome, badge)}
      </div>
    );
  };

  /**
   * 라이브 경기 판정·표시 정보 — REST(adaptFixtureTable: matchStatus/liveTime/score) 와
   * WS 실시간 병합(SportsPage: status/matchTime/score) 어느 쪽 값이든 보여 준다.
   */
  const liveInfoOf = (match) => {
    const isLive = match.matchStatus === 'live' || match.status === 'live' || match.status === 'inprogress' || isLiveTab;
    if (!isLive) return null;
    return {
      score: formatScore(match.score),
      time: match.matchTime || match.liveTime || null,
    };
  };

  /** 행 첫 칸: 시작 시각(프리매치) / LIVE 배지 + 스코어 + 진행 시각(라이브) */
  const renderTimeCell = (match) => {
    const live = liveInfoOf(match);
    if (live) {
      return (
        <div className="time">
          <span className="lv">LIVE</span>
          {live.score && <b className="sc">{live.score}</b>}
          {live.time && <span className="lt">{live.time}</span>}
        </div>
      );
    }
    return <div className="time">{formatMatchDate(match.startTime)}</div>;
  };

  /** 리그 카드 머리(시안 .league-h): 종목 · 국가 칩 + 국가명 · 리그 아이콘 + 리그명 */
  const renderLeagueLabel = (league, sportType) => (
    <>
      {renderSportBadge(sportType)}
      <span className="div">|</span>
      <CountryChip name={league?.country} code={league?.countryCode} />
      <span className="league-country">{league?.country || '기타'}</span>
      <span className="div">|</span>
      <span className="flag">{renderLeagueIcon(league, sportType)}</span>
      <span className="league-name">{league?.name || '-'}</span>
    </>
  );

  /**
   * 개별 경기 렌더링 — 경기 1개를 "마켓별 행"들로 나열 (blk-111 참고 패턴).
   *
   * · 기본 상태: 메인 마켓 1행만 표시, "+N" 배지에 추가 마켓 수 노출
   * · 배지 클릭 시 toggleExpandedMatch 호출 → 상세 API 로드 후 모든 마켓을
   *   행 단위로 펼침 (expandedMatches Set 에 matchId 저장)
   * · 핸디캡 탭(isHandicapMode): 자동 전체 펼침 + 핸디캡 마켓만 필터
   * · 언오버 탭(isOverUnderMode): 자동 전체 펼침 + over_under 마켓만 필터
   * · 승무패 탭(isWinnerMode): 메인 1X2 만, 펼침 배지 미노출
   *
   * 첫 행은 .mrow(시각 | 홈 | 무·기준 | 원정 | +N), 나머지 마켓은 .markets 패널의 .mkrow(마켓명 | 홈 | 무·기준 | 원정).
   * 마켓 유형별 라벨/배지/중앙값은 MarketRowUtils.marketToRow() 에서 결정.
   *
   * @param {object} match - 경기 데이터 (adaptFixtureTable 형식)
   * @param {object} options - { showLeagueTag: boolean } — 시간순 모드에서 경기마다 리그 태그 표시
   */
  const renderMatchCard = (match, options = {}) => {
    const { showLeagueTag = false } = options;

    // 펼침 상태 판정 — 사용자 클릭 or 핸디캡/언오버 탭 자동 펼침
    const isExpandedManual = expandedMatches.has(match.matchId);
    const isExpanded = isExpandedManual || needsDetailAutoload;
    const cached = detailMarketsCache[match.matchId] || null;
    const isLoadingExpansion = loadingDetail === match.matchId
      || (needsDetailAutoload && !cached);

    // 표시할 마켓 목록 결정 — 펼침 상태에 따라 메인만 or 전체
    let displayMarkets = pickMarketsForRows(match, cached, isExpanded);

    // 핸디캡 탭: 핸디캡 계열만 남김
    if (isHandicapMode) {
      displayMarkets = displayMarkets.filter(m => isHandicapMarket(m));
    }
    // 언오버 탭: 백엔드가 분류한 marketType='over_under' 만 남김
    if (isOverUnderMode) {
      displayMarkets = displayMarkets.filter(m => m?.marketType === 'over_under');
    }
    // 승무패 탭: 메인 마켓 1개만 (펼침 없음)
    if (isWinnerMode) {
      displayMarkets = displayMarkets.slice(0, 1);
    }
    // [2026-05-06 스페셜 = 언오버 패턴] 스페셜 탭: 마켓관리에서 운영자가 분류한
    //   feedType IN ('special','combo_special') 마켓만 남긴다. user-api transformMarkets 가
    //   sports_markets.feed_type 으로 부착한 feedType 필드 사용. feedType 누락 시 폴백 통과.
    //   빈 결과 fixture 는 아래 displayMarkets.length===0 분기에서 숨김 (언오버와 동일).
    if (isSpecialTab) {
      displayMarkets = displayMarkets.filter(m => {
        const ft = m?.feedType;
        if (!ft) return false; // 스페셜 탭은 명시적 분류만 노출 (폴백 통과 X)
        return ft === 'special' || ft === 'combo_special';
      });
    }
    // [2026-05-06 피드유형 재설계] 조합베팅 탭: 운영자가 마켓관리에서
    //   "조합" 또는 "조합+스페셜" 로 분류한 마켓만 노출.
    //   - feedType 필드는 user-api transformMarkets 가 sports_markets.feed_type 으로 부착.
    //   - feedType 누락(서버 응답이 구버전이거나 메인 마켓 합성으로 부착 전) 시 통과 (기본값 폴백).
    //   - 단일 마켓 탭(승무패/핸디캡/언오버)은 위 분기에서 이미 처리되었으므로 본 필터 미적용.
    //   - 스페셜/라이브 탭에서는 effectiveMarket 이 'combo' 로 강제되지만 본 필터는 미적용
    //     (isPrematchNormal 조건). 스페셜 탭은 서버에서 이미 special/combo_special 마켓을
    //     필터해 내려보내므로 클라이언트 추가 필터링이 모든 마켓을 제거하는 회귀를 차단.
    if (isPrematchNormal && marketFilter === 'combo') {
      displayMarkets = displayMarkets.filter(m => {
        const ft = m?.feedType;
        if (!ft) return true; // 폴백 — 메타 미부착 마켓은 통과
        return ft === 'combo' || ft === 'combo_special';
      });
    }

    // WHY: displayMarkets 가 비어있지 않더라도 marketToRow 가 모든 마켓에 대해 null 을
    //   반환하면(예: outcomes 부족·비대칭 마켓만 남은 핸디캡 탭) 정상 카드 분기로
    //   진입해 league-tag 만 노출되고 본문 row 가 0개인 "껍데기 카드" 가 생긴다.
    //   이로 인해 핸디캡·언오버 탭에서 매치 본문 없는 league-tag 줄이 헤더처럼 보이는
    //   결함이 발생. 탭 필터 직후 marketToRow 사전 평가로 렌더 가능한 마켓만 남겨,
    //   아래 displayMarkets.length === 0 분기가 동일하게 매치카드를 null 반환하도록
    //   유도한다(추가 분기 신설 없이 기존 가드 재사용).
    displayMarkets = displayMarkets.filter(m => marketToRow(m) !== null);

    // "+N" 배지 표시 여부 — combo 탭에서만, 추가 마켓이 있을 때만.
    //
    // [2026-04-20 수정] 이전 구현은 cached(상세 API) 에서 메인 마켓을 제외할 때
    //   `match.markets[0].marketId` (목록 API 의 buildMainMarket 합성 ID) 와
    //   `cached[i].marketId` (상세 API 의 transformMarkets 합성 ID) 를 직접 비교했다.
    //   두 합성 ID 는 구조는 동일(`mk-{fixtureId}-{marketKey}`)하나 marketKey 소스가
    //   목록(row.main_market_key, 폴백 '1')과 상세(market.market_key)에서 다를 수
    //   있어 매칭 실패 → 메인이 제외되지 않고 extraCount 가 1 과다 노출됨.
    //
    //   해결: cached 내부에서 marketType==='match_result' 인 "모든" 메인 마켓을 제외.
    //         단순 findMainMarket.find() 는 첫 매치 1건만 제외하는데, 실제 상세 응답에는
    //         Match Winner(3way)·Home/Away(2way) 가 동시에 match_result 로 오는 종목이
    //         많아(농구·야구·e스포츠 등) 1 과다 표시가 여전히 발생한다. 따라서
    //         `marketType !== 'match_result'` 로 필터해 카테고리 전체를 제외한다.
    //   cached 미로드 시(펼치기 전) 는 admin-api 가 PG 로 재계산한 match.marketCount
    //   를 사용 — 이 값은 활성 goalserve_id 기반 메인 제외 카운트로 이미 정확.
    // [2026-05-12] +N 산식 — 펼침 상태와 무관하게 화면에 표시될 카드 수와 1:1 일치.
    //   사용자 의도(확정): +N 은 펼친 후 메인 영역에 노출되는 "추가" 마켓 카드 수.
    //     · 펼치기 전 → 같은 숫자
    //     · 펼친 후 → 같은 숫자
    //     · 접은 후 → 같은 숫자
    //
    //   산식:
    //     · cached 가 로드되어 있으면 cached 자체에서 산정(펼침/접힘 상태 무관 동일값 보장).
    //     · 탭별 필터(combo/special/언오버/핸디캡) 를 displayMarkets 와 동일하게 적용.
    //     · marketType==='match_result' 제외(메인 셀)
    //     · outcomes.length < 2 제외(marketToRow 가 null → 행 미렌더)
    //
    //   cached 미로드(목록 진입 직후, 자동 펼침 종목 아님) 시에는 admin-api fallback 사용.
    //   admin-api PG 도 동일 산식으로 통일 작업 진행 중 — 향후 fallback 도 정확.
    const extraCount = (() => {
      if (!showMoreBadge) return 0;
      if (cached && cached.length > 0) {
        let cnt = 0;
        for (const m of cached) {
          if (m?.marketType === 'match_result') continue;
          if (!Array.isArray(m?.outcomes) || m.outcomes.length < 2) continue;
          // 탭별 필터 — displayMarkets 산정과 동일하게 적용
          if (isHandicapMode && !isHandicapMarket(m)) continue;
          if (isOverUnderMode && m?.marketType !== 'over_under') continue;
          if (isSpecialTab) {
            const ft = m?.feedType;
            if (!ft) continue;
            if (ft !== 'special' && ft !== 'combo_special') continue;
          }
          if (isPrematchNormal && marketFilter === 'combo') {
            const ft = m?.feedType;
            if (ft && ft !== 'combo' && ft !== 'combo_special') continue;
          }
          cnt++;
        }
        return cnt;
      }
      return match.marketCount > 0 ? match.marketCount : 0;
    })();
    const showExpandBtn = showMoreBadge && (extraCount > 0 || isExpandedManual);

    // 시간순 모드의 경기 카드 머리(리그 태그) — 시안 리그 카드 머리와 같은 생김새, 접기 없음
    const leagueTag = showLeagueTag ? (
      <div className="league-h tag">
        {renderLeagueLabel(match.league, match.sportType)}
      </div>
    ) : null;

    // 마켓이 아예 없는 경우(데이터 지연/오류) 빈 1행 안내
    if (displayMarkets.length === 0) {
      // [2026-04-21] 언오버/핸디캡 탭은 해당 마켓이 없는 경기를 아예 숨긴다.
      //   Why: 이 두 탭은 특정 마켓 전용 뷰이므로 해당 마켓 없는 경기를
      //     "언오버 배당 없음" 행으로 나열하면 사용자에게 정보 가치가 없고
      //     가독성만 저하된다. 로딩 중(isLoadingExpansion)에는 "불러오는 중..." 을
      //     유지해 데이터 수신 대기를 명시한다.
      //   How to apply: isHandicapMode / isOverUnderMode 전용. combo/winner 모드는
      //     메인 1X2 일시 지연 안내를 위해 기존 "배당 없음" 문구를 유지한다.
      // [2026-05-06 스페셜 = 언오버 패턴] 스페셜 탭도 빈 결과 fixture 숨김 (언오버 동일).
      if ((isHandicapMode || isOverUnderMode || isSpecialTab) && !isLoadingExpansion) {
        return null;
      }
      return (
        <div key={match.matchId} className={showLeagueTag ? 'league league-single' : 'match-group'}>
          {leagueTag}
          <div className="rows">
            <div className="mrow mrow-empty">
              {renderTimeCell(match)}
              <div className="odd-grp odd-home static">
                <div className="odd-team">
                  {renderTeamLogo(match.homeTeam, match.sportType)}
                  <span className="tm">{match.homeTeam.name}</span>
                </div>
              </div>
              <div className="mrow-note">
                {isLoadingExpansion
                  ? (isHandicapMode ? '핸디캡 배당 불러오는 중...'
                    : isOverUnderMode ? '언오버 배당 불러오는 중...'
                    : '마켓 로딩 중...')
                  : '배당 없음'}
              </div>
              <div className="odd-grp odd-away static">
                <div className="odd-team">
                  {renderTeamLogo(match.awayTeam, match.sportType)}
                  <span className="tm">{match.awayTeam.name}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // 첫 행(.mrow)과 추가 마켓 행(.mkrow) — 같은 홈/무·기준/원정 칸 렌더러를 쓴다
    const renderCenterCell = (match, market, row) => {
      const centerSelected = row.centerOutcome
        ? isSelected(row.centerOutcome.outcomeId)
        : false;
      const centerCls = [
        'odd-grp',
        'odd-center',
        row.centerOutcome ? '' : 'static',
        centerSelected ? 'on' : '',
      ].filter(Boolean).join(' ');
      return (
        <div className={centerCls}>
          {row.centerOutcome
            ? (
              <>
                <span className="tm">{row.centerOutcome.name}</span>
                {renderOddsButton(match, market, row.centerOutcome, null)}
              </>
            )
            : <span className="cval">{row.centerValue}</span>
          }
        </div>
      );
    };

    const rowsEl = displayMarkets.map((market, idx) => {
      // 마켓 유형에 맞춘 1행 표시 데이터 (홈/원정 outcome, 배지, 중앙값)
      const row = marketToRow(market);
      if (!row) return null;  // 비정상 마켓은 건너뜀 (레이아웃 유지)

      const isFirst = idx === 0;
      const rowKey = `${match.matchId}-${market.marketId}`;

      if (isFirst) {
        return (
          <div key={rowKey} className="mrow">
            {/* 1. 시작 시각 / LIVE·스코어·진행 시각 */}
            {renderTimeCell(match)}
            {/* 2. 홈: 팀 블록(클릭 = 홈 배당 담기) + 마켓 라벨 + 홈 배당 */}
            {renderSideGroup(match, market, row.homeOutcome, row.homeBadge, 'home', match.homeTeam, row.homeLabel)}
            {/* 3. 중앙: 무 배당(클릭 가능) 또는 기준(라인)/VS */}
            {renderCenterCell(match, market, row)}
            {/* 4. 원정: 팀 블록 + 마켓 라벨 + 원정 배당 */}
            {renderSideGroup(match, market, row.awayOutcome, row.awayBadge, 'away', match.awayTeam, row.awayLabel)}
            {/* 5. "+N"/접기 — 첫 행에만, combo 모드에만 */}
            <div className="mrow-more">
              {showExpandBtn && (
                <button
                  type="button"
                  className={`more ${isExpandedManual ? 'on' : ''}`}
                  onClick={(e) => toggleExpandedMatch(match.matchId, e)}
                  aria-expanded={isExpandedManual}
                  aria-label={isExpandedManual ? '마켓 접기' : '추가 마켓 보기'}
                >
                  {isLoadingExpansion
                    ? '...'
                    : (isExpandedManual ? '▲ 접기' : `+${extraCount > 99 ? '99+' : extraCount}`)}
                </button>
              )}
            </div>
          </div>
        );
      }

      return (
        <div key={rowKey} className="mkrow">
          <span className="name">{market.marketName}</span>
          {renderSideGroup(match, market, row.homeOutcome, row.homeBadge, 'home', match.homeTeam, row.homeLabel)}
          {renderCenterCell(match, market, row)}
          {renderSideGroup(match, market, row.awayOutcome, row.awayBadge, 'away', match.awayTeam, row.awayLabel)}
        </div>
      );
    });

    const mainRow = rowsEl.length > 0 ? rowsEl[0] : null;
    const extraRows = rowsEl.slice(1).filter(Boolean);

    return (
      <div key={match.matchId} className={showLeagueTag ? 'league league-single' : 'match-group'}>
        {/* 시간순 모드에서만 표시되는 리그/국가 태그 (경기마다 상단) */}
        {leagueTag}
        <div className="rows">
          {mainRow}
          {/* 추가 마켓(펼침·자동 펼침) — 시안 .markets 패널 */}
          {extraRows.length > 0 && (
            <div className="markets">
              {extraRows}
            </div>
          )}
        </div>
      </div>
    );
  };

  /** 리그/국가 그룹 카드 머리의 오른쪽(경기 수 + 접기 화살표) */
  const renderGroupRight = (count, isCollapsed) => (
    <>
      <span className="cnt">{count}경기</span>
      <svg className="chev" viewBox="0 0 24 24" fill="none" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
        <path d="M6 9l6 6 6-6" />
      </svg>
      <span className="sr-only">{isCollapsed ? '펼치기' : '접기'}</span>
    </>
  );

  return (
    <div className="matchlist">
      {/* ── 시간순 모드: 그룹 없이 플랫 리스트 ── */}
      {sortMode === 'time' && timeSortedMatches.slice(0, maxVisible).map(match =>
        renderMatchCard(match, { showLeagueTag: true })
      )}

      {/* ── 리그별 모드: leagueId 기반 그룹핑 (점진 로딩 적용) ── */}
      {sortMode === 'league' && limitedGrouped.map(group => {
        const isCollapsed = collapsedLeagues.has(group.league.leagueId);
        // WHY: 핸디캡·언오버 탭에서 마켓 필터 후 가시 경기가 없는 그룹은
        //   헤더만 남아 사용자 혼동 유발 → 그룹 내 가시 경기 수 0이면 그룹 전체 숨김.
        //   renderMatchCard 결과를 먼저 수집하고 null만 걸러낸 뒤 헤더 렌더 여부를 결정.
        //   combo/winner 탭에서는 renderMatchCard 가 null 반환하지 않으므로 기존 동작 유지.
        const renderedCards = group._visibleMatches.map(match => renderMatchCard(match));
        const visibleCards = renderedCards.filter(card => card !== null);
        if (visibleCards.length === 0) return null;
        return (
        <div key={group.league.leagueId} className={`league ${isCollapsed ? 'closed' : ''}`}>
          {/* ── 리그 카드 머리 ── 종목 | 국가 칩 국가명 | 리그아이콘 리그명 · N경기 ▼ */}
          <div
            className="league-h"
            onClick={() => toggleLeague(group.league.leagueId)}
            role="button"
            tabIndex={0}
            aria-expanded={!isCollapsed}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleLeague(group.league.leagueId); } }}
          >
            {renderLeagueLabel(group.league, group.sportType)}
            {renderGroupRight(group.matches.length, isCollapsed)}
          </div>

          {/* ── 경기 목록 ── 접힘 상태에 따라 표시(.league.closed .rows 숨김) */}
          <div className="rows">
            {visibleCards}
          </div>
        </div>
        );
      })}

      {/* ── 국가별 모드: country 기반 그룹핑 (점진 로딩 적용) ── */}
      {sortMode === 'country' && limitedCountry.map(group => {
        // 국가별 접기/펼치기는 country 문자열을 키로 사용
        const isCollapsed = collapsedLeagues.has(group.country);
        // WHY: 핸디캡·언오버 탭에서 마켓 필터 후 가시 경기가 없는 그룹은
        //   헤더만 남아 사용자 혼동 유발 → 그룹 내 가시 경기 수 0이면 그룹 전체 숨김.
        //   renderMatchCard 결과를 먼저 수집하고 null만 걸러낸 뒤 헤더 렌더 여부를 결정.
        //   combo/winner 탭에서는 renderMatchCard 가 null 반환하지 않으므로 기존 동작 유지.
        const renderedCards = group._visibleMatches.map(match => renderMatchCard(match, { showLeagueTag: true }));
        const visibleCards = renderedCards.filter(card => card !== null);
        if (visibleCards.length === 0) return null;
        const firstLeague = group._visibleMatches[0]?.league;
        return (
        <div key={group.country} className={`league league-country ${isCollapsed ? 'closed' : ''}`}>
          {/* ── 국가 카드 머리 ── 국가 칩 국가명 · N경기 ▼ */}
          <div
            className="league-h"
            onClick={() => toggleLeague(group.country)}
            role="button"
            tabIndex={0}
            aria-expanded={!isCollapsed}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleLeague(group.country); } }}
          >
            <CountryChip name={group.country} code={firstLeague?.countryCode} />
            <span className="league-country">{group.country}</span>
            {renderGroupRight(group.matches.length, isCollapsed)}
          </div>

          {/* ── 경기 목록 ── 접힘 상태에 따라 표시 */}
          {/* 국가별 모드는 한 그룹에 여러 종목이 섞일 수 있어, 경기마다 종목/리그 태그 표시 */}
          <div className="rows">
            {visibleCards}
          </div>
        </div>
        );
      })}

      {/* ── 점진 로딩 센티널 ── IntersectionObserver가 감지하면 다음 배치 로드 */}
      {hasMore && <div ref={sentinelRef} style={{ height: 1 }} />}
    </div>
  );
};

export default SportsMatchList;
